import { useMemo } from 'react';

import { mapDeltaTypeToCounting } from './procedural-helpers';
import { resolveProceduralDate } from '../../../../../../../../backend/src/matter/procedural-timeline/date-resolution';
import {
  DeltaIntervalType,
  ProceduralJurisdiction,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { useProceduralHolidays } from '@/api/queries/useProceduralHolidays';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { instantToCivilUtc } from '@/new/components/timeline-calculator/civil-date';
import { COUNTING_OPTIONS } from '@/new/stores/timeline-calculator-store';

// Only jurisdictions with real counting rules + holiday data are computed.
const SUPPORTED_JURISDICTIONS: ProceduralJurisdiction[] = [
  ProceduralJurisdiction.EnglandAndWales,
  ProceduralJurisdiction.USFederal,
];

type CalculatedDateState =
  | { kind: 'none' }
  | { kind: 'loading' }
  | { kind: 'calculable'; calculatedDate: Date }
  | { kind: 'match'; calculatedDate: Date }
  | { kind: 'mismatch'; calculatedDate: Date };

interface CalculatedDateInput {
  matterId: string | undefined;
  /** The anchor (source) event's start date. */
  anchorStartDateTime: Date | string | null;
  /** Day offset from the anchor; the calculator only counts days. */
  days: number | null;
  deltaIntervalType: DeltaIntervalType | null;
  jurisdiction: ProceduralJurisdiction | null;
  /** This row's own (effective) start date, compared against the calculation. */
  currentStartDateTime: Date | string | null;
}

export function useCalculatedDate({
  matterId,
  anchorStartDateTime,
  days,
  deltaIntervalType,
  jurisdiction,
  currentStartDateTime,
}: CalculatedDateInput): CalculatedDateState {
  const anchorCivil = instantToCivilUtc(anchorStartDateTime);
  const hasValidDays = days !== null && Number.isFinite(days) && days !== 0;
  const jurisdictionSupported = jurisdiction !== null && SUPPORTED_JURISDICTIONS.includes(jurisdiction);
  const canCalculate = anchorCivil !== null && hasValidDays && deltaIntervalType !== null && jurisdictionSupported;

  const countingKey = canCalculate ? mapDeltaTypeToCounting(deltaIntervalType, jurisdiction) : null;
  const option = countingKey !== null ? (COUNTING_OPTIONS.find((o) => o.key === countingKey) ?? null) : null;

  const anchorYear = anchorCivil?.getUTCFullYear() ?? new Date().getUTCFullYear();
  const { data: holidays } = useProceduralHolidays({
    matterId: canCalculate ? matterId : undefined,
    jurisdiction: option?.jurisdiction ?? 'uk-ew',
    fromYear: anchorYear - 1,
    toYear: anchorYear + 2,
  });

  return useMemo<CalculatedDateState>(() => {
    if (!canCalculate || option === null || anchorCivil === null || days === null) return { kind: 'none' };
    if (holidays === undefined) return { kind: 'loading' };

    let calculatedDate: Date;
    try {
      const result = resolveProceduralDate({
        startDate: anchorCivil,
        interval: { days },
        jurisdiction: option.jurisdiction,
        dayCount: option.dayCount,
        holidays,
      });
      calculatedDate = instantToCivilUtc(result.resultDate) ?? result.resultDate;
    } catch {
      return { kind: 'none' };
    }

    const currentCivil = instantToCivilUtc(currentStartDateTime);
    if (currentCivil === null) return { kind: 'calculable', calculatedDate };
    if (currentCivil.getTime() === calculatedDate.getTime()) return { kind: 'match', calculatedDate };
    return { kind: 'mismatch', calculatedDate };
    // anchorCivil is recreated each render, so the memo keys off the stable
    // source values (anchorStartDateTime, countingKey) instead.
  }, [canCalculate, countingKey, holidays, anchorStartDateTime, days, currentStartDateTime]);
}

/** Clicking the chip/icon opens the timeline calculator. */
export function CalculatedDateNotice({
  state,
  onOpenCalculator,
}: {
  state: CalculatedDateState;
  onOpenCalculator: () => void;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenCalculator();
  };

  if (state.kind === 'calculable') {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={handleClick}
        className="h-auto p-0"
        aria-label="Open the timeline calculator to resolve this date"
      >
        <Chip className="cursor-pointer rounded-full border border-emerald-200 bg-emerald-50 font-normal text-emerald-700 hover:bg-emerald-100">
          <Icon className="lucide--calculator text-emerald-500" />
          Can be calculated
        </Chip>
      </Button>
    );
  }
  return null;
}
